import React, { useState, useEffect, useRef, useMemo } from 'react';
import { classNames } from 'primereact/utils';
import { InputText } from 'primereact/inputtext';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { useFormik } from 'formik';
import { Toast } from 'primereact/toast';
import { translatedMessage } from '../../service/LanguageService';
import { CompetitionService } from '../../service/CompetitionService';

import { Calendar } from 'primereact/calendar';
import GeneralUtils from '../../utilities/GeneralUtils';
import EnumService from '../../service/EnumService';
import { Dropdown } from 'primereact/dropdown';

const emptyRecord = {
    id: null,
    examType: '',
    examDate: '',
    examName: '',
    examLocation: ''
};

const CompetitionJobExamEditDialog = (props) => {
    const [exam, setExam] = useState(emptyRecord);
    // const [isNew, setIsNew] = useState(false);
    const [showExamName, setShowExamName] = useState(false);
    const [examTypes, setExamTypes] = useState([]);

    const toast = useRef(null);
    const competitionService = useMemo(() => new CompetitionService(), []);

    useEffect(() => {
        let _exam = props?.value ? GeneralUtils.emptyInNullForObjectFields(props?.value) : { ...emptyRecord, competition: props?.competition };
        // setIsNew(props?.value ? false : true);

        const getExamTypes = async () => {
            let _types = await EnumService.getEnumByName('CompetitionExamType');
            setExamTypes(_types.filter((e) => e.value !== 'WRITTEN_EXAM'));
        };

        // console.log(_exam);
        setExam(_exam);
        setShowExamName(_exam?.examType === 'SUPPLEMENTARY_EXAM');
        getExamTypes()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props]);

    const hideDialog = () => {
        if (typeof props.visibleSetter === 'function') {
            props.visibleSetter(false);
        }
        formik.resetForm();
    };

    const saveExam = (data) => {
        competitionService.saveCompetitionJobExam(data)
            .then((savedExam) => {
                if (typeof props.afterSave === 'function') {
                    props.afterSave(savedExam.competitionCalendar, true, null);
                }
                hideDialog();
            })
            .catch((error) => {
                // console.log("error: ", error);
                props.afterSave(null, false, error);
            });
    };

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: exam ? exam : { ...emptyRecord, competition: props?.competition },
        validate: (data) => {
            let errors = {};

            // console.log("validation: ", data);

            if (!data.examType || data.examType.trim() === '') {
                errors.examType = translatedMessage('form.error.competition.examType.required');
            }
            if (data.examType === 'SUPPLEMENTARY_EXAM' && (!data.examName || data.examName.trim() === '')) {
                errors.examName = translatedMessage('form.error.competition.examName.required');
            }
            if (!data.examDate) {
                errors.examDate = translatedMessage('form.error.competition.examDate.required');
            }
            if (!data.examLocation || data.examLocation.trim() === '') {
                errors.examLocation = translatedMessage('form.error.competition.examLocation.required');
            }

            if (props.competition?.writtenTestDate && (data.examType === 'IT_EXAM' || data.examType === 'LANGUAGE_EXAM' || data.examType === 'SUPPLEMENTARY_EXAM') &&
                GeneralUtils.hoursBetweenDates(data.examDate, props.competition?.writtenTestDate) < 1) {

                errors.examDate = translatedMessage('form.error.competition.examDate.toLate');
            }

            return errors;
        },
        onSubmit: (data) => {
            let _data = { ...GeneralUtils.nullIfEmptyForObjectFields(data) };
            // console.log("exam calendar saved: ", _data);
            saveExam(_data);
        }
    });

    const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);
    const getFormErrorMessage = (name) => {
        return isFormFieldValid(name) && <small className="p-error text-align-left">{formik.errors[name]}</small>;
    };

    const formDialogFooter = (
        <>
            <Button label={translatedMessage('generic.cancel')} icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label={translatedMessage('generic.save')} icon="pi pi-save" className="p-button-text" form="field-form" type="submit" 
                disabled={!props.competition.writtenTestDate}/>
        </>
    );

    const handleTypeChange = (_type) => {
        formik.setFieldValue('examType', _type.value);
        setShowExamName(_type.value === 'SUPPLEMENTARY_EXAM');
    }

    return (
        <>
            <Toast ref={toast} />
            <Dialog
                visible={props?.visible}
                header={translatedMessage('competition.calendar.dialog.title')}
                modal
                className="pcn-dialog-small"
                footer={formDialogFooter}
                onHide={hideDialog}
            >
                <form id="field-form" onSubmit={formik.handleSubmit}>
                    <div className='grid'>
                        <div className='col-12'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <Dropdown
                                        id="examType"
                                        name="examType"
                                        value={formik.values.examType}
                                        onChange={handleTypeChange}
                                        options={examTypes}
                                        itemTemplate={(e) => e.label}
                                        scrollHeight="400px"
                                        className={classNames({ 'p-invalid': isFormFieldValid('examType') })}
                                    />
                                    <label htmlFor="examType" className={classNames({ 'p-error': isFormFieldValid('examType') })}>
                                        {translatedMessage('competition.exam.type')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('examType')}
                        </div>

                        {showExamName &&
                            <div className='col-12'>
                                <div className="p-inputgroup">
                                    <span className="p-float-label">
                                        <InputText type="text" name="examName" id="examName"
                                            value={formik.values.examName} onChange={formik.handleChange}
                                            className={classNames({ 'p-invalid': isFormFieldValid('examName') })}
                                        />
                                        <label htmlFor="examName" className={classNames({ 'p-error': isFormFieldValid('examName') })}>
                                            {translatedMessage("competition.exam.name")} *
                                        </label>
                                    </span>
                                </div>
                                {getFormErrorMessage('examName')}
                            </div>
                        }

                        <div className='col-12'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <Calendar
                                        id="examDate"
                                        name="examDate"
                                        value={formik.values.examDate}
                                        onChange={formik.handleChange}
                                        dateFormat="dd-mm-yy"
                                        showTime
                                        hourFormat="24"
                                        stepMinute={10}
                                        onShow={() => { if (!formik.values.examDate) { formik.setFieldValue("examDate", GeneralUtils.roundDateTimeHour(0)) } }}
                                        className={classNames({ 'p-invalid': isFormFieldValid('examDate') })}
                                    />
                                    <label htmlFor="examDate" className={classNames({ 'p-error': isFormFieldValid('examDate') })}>
                                        {translatedMessage('competition.exam.dateHour')} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('examDate')}
                        </div>

                        <div className='col-12'>
                            <div className="p-inputgroup">
                                <span className="p-float-label">
                                    <InputText type="text" name="examLocation" id="examLocation"
                                        value={formik.values.examLocation} onChange={formik.handleChange}
                                        className={classNames({ 'p-invalid': isFormFieldValid('examLocation') })}
                                    />
                                    <label htmlFor="examLocation" className={classNames({ 'p-error': isFormFieldValid('examLocation') })}>
                                        {translatedMessage("competition.exam.location")} *
                                    </label>
                                </span>
                            </div>
                            {getFormErrorMessage('examLocation')}
                        </div>



                    </div>
                </form>
            </Dialog >
        </>
    );
};

export default CompetitionJobExamEditDialog;
